/**
 * Sticky navigation background toggle
 *
 * @param {HTMLElement} section DOM node
 */
function handleStickyNavigation(section) {
  const observer = new IntersectionObserver(
    (entries) => {
      // eslint-disable-next-line
      for (const entry of entries) {
        if (entry.target.getBoundingClientRect().top < 200) entry.target.classList.add('is-sticky');
        else entry.target.classList.remove('is-sticky');
      }
    },
    {
      root: null,
      rootMargin: `-200px 0px ${-1 * window.innerHeight + 200}px 0px`,
      threshold: 0,
    }
  );
  observer.observe(section);
}

/**
 *  Resize observer
 *
 */
function addResizeObserver() {
  const resizeObserver = new ResizeObserver((entries) => {
    // eslint-disable-next-line
    for (let entry of entries) {
      if (entry.contentRect.width !== entry.target.querySelector(`.swiper-wrapper`).scrollWidth) {
        entry.target.swiper.enable();
      } else {
        entry.target.swiper.disable();
      }
    }
  });
  resizeObserver.observe(document.querySelector('.scroll-to-section .swiper'));
}

/**
 *  Intersection observer
 *
 * @param {HTMLElement[]} sections DOM nodes
 * @param {HTMLElement[]} links navigation links
 */
function addIntersectionObserver(sections, links) {
  /**
   * @param {HTMLElement[]} entries DOM nodes
   * @param {HTMLElement[]} navigationLinks navigation links
   */
  const handleIntersection = (entries, navigationLinks) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        navigationLinks.forEach((link) => link.classList.remove('highlight'));
        const link = document.querySelector(`.scroll-to-section a[href="#${entry.target.id}"]`);
        if (link) {
          link.classList.add('highlight');
          const swiper = document.querySelector('.scroll-to-section .swiper');
          if (swiper.clientWidth !== document.querySelector('.scroll-to-section .swiper-wrapper').scrollWidth) {
            swiper.swiper.slideTo(link.dataset.index);
          }
        }
      }
    });
  };
  const observer = new IntersectionObserver((entries) => handleIntersection(entries, links), {
    root: null,
    rootMargin: `-250px 0px ${-1 * window.innerHeight + 250}px 0px`,
    threshold: 0,
  });
  sections.forEach((section) => observer.observe(section));
}

/**
 * Create DOM links
 *
 * @param {HTMLElement[]} sections DOM nodes
 * @returns {HTMLElement[]} array of links
 */
function createNavigationLinks(sections) {
  return sections.map((section, index) => {
    const a = document.createElement('a');
    a.innerHTML = section.querySelector('[data-section-title]')?.dataset.sectionTitle || section.querySelector('h2')?.textContent || '';
    a.href = `#${section.id}`;
    a.dataset.index = index;
    a.classList.add('swiper-slide');
    if (index === 0) a.classList.add('highlight');
    return a;
  });
}

/**
 * Return siblings to element
 *
 * @param {HTMLElement} element DOM node
 * @returns {HTMLElement[]} siblings to element
 */
function getNextSiblingSections(element) {
  const siblings = [];
  let el = element.nextElementSibling;
  while (el) {
    siblings.push(el);
    el = el.nextElementSibling;
  }
  // don't include e.g. product recommendations
  if (!document.querySelector('.scroll-to-section.scroll-to-section--include-last-section')) {
    siblings.pop();
  }
  return siblings;
}

/**
 * Updates UI with new navigation links
 *
 * @param {HTMLElement[]} navigationLinks navigation links
 */
function updateNavigationUI(navigationLinks) {
  const container = document.querySelector('.scroll-to-section');
  const template = document.getElementById('code-template').content.cloneNode(true);
  navigationLinks.forEach((link) => template.querySelector('.swiper-wrapper').appendChild(link));
  container.appendChild(template);
}

/**
 * Scroll to section after clicking adequate link
 *
 * @param {HTMLElement[]} navigationLinks navigation links
 */
function addScrollBehavior(navigationLinks) {
  navigationLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetSection = document.querySelector(e.target.getAttribute('href'));
      if (!targetSection) return;
      const sectionTop = targetSection.getBoundingClientRect().top;
      const navHeight = document.querySelector('.scroll-to-section')?.offsetHeight || 0;
      const header = document.querySelector('.shopify-section-header-sticky');
      const headerHeight = header ? header.offsetHeight : 0;
      window.scrollTo({
        top: window.scrollY + sectionTop - navHeight - (sectionTop < 0 ? headerHeight : 0),
        behavior: 'smooth',
      });
    });
  });
}

/**
 * Appends navigation links and initializes observer
 */
function setupNavigation() {
  const parentSection = document.querySelector('.scroll-to-section')?.closest('section');
  if (!parentSection) return;
  const siblingSections = getNextSiblingSections(parentSection);
  const navigationLinks = createNavigationLinks(siblingSections);
  addScrollBehavior(navigationLinks);
  updateNavigationUI(navigationLinks);
  addIntersectionObserver(siblingSections, navigationLinks);
  addResizeObserver();
  handleStickyNavigation(parentSection);
}

/**
 * Init
 */
function init() {
  setupNavigation();
}

init();
